import type { ButtonPriority } from '@timelyapp/react';
import { useMountEffect } from '@timelyapp/react';
import FilterGroups from 'components/dropdowns/FilterGroups';
import { matchProjectOption } from 'components/ProjectSelect';
import SelectOption from 'components/ProjectSelect/SelectOption';
import Search from 'components/Search';
import type { ProjectDropdownOption } from 'factories/projects';
import { incompleteOption } from 'factories/projects';
import { isNumber, omit } from 'lodash-es';
import React, { useCallback } from 'react';
import type { ResolveThunks } from 'react-redux';
import { connect, useDispatch } from 'react-redux';
import { fetchProjectIdSmartly, searchProjects } from 'redux/modules/projects.actions';
import type { ActAsUserIdOwnProps } from 'redux/selectors/projects';
import { makeGetProjectSearchOptions } from 'redux/selectors/projects';
import type { IState } from 'redux/state';
import type { FilterGroupValues } from 'types/project';
import type { ThunkDispatch } from 'types/redux';
import { notNull } from 'utils/typesafe';

type ExternalProps = ActAsUserIdOwnProps & {
  onChange: (id: number | null) => void;
  value?: number | null;
  name?: string;
  placeholder?: string;
  variant?: 'default' | 'inHeader' | 'inHourEditor';
  className?: string;
  shrinkOnBlur?: boolean;
  buttonPriority?: ButtonPriority;
  autoFocus?: boolean;
  loadFullProject?: boolean;
  /** Replace the search icon with something else? */
  inputIcon?: React.ReactNode;
  recentProjects?: ProjectDropdownOption;
  pinnedProjects?: ProjectDropdownOption;
  hideArchivedProjects?: boolean;
  /** When set, will turn options into links on ${optionAsLinkFromBasePath}${option.value} */
  optionAsLinkFromBasePath?: string;
  filterGroup?: FilterGroupValues;
  onFilterGroupChange?: (filterGroup: FilterGroupValues) => void;
};

const mapActionsToProps = {
  searchProjects,
};
type ActionProps = ResolveThunks<typeof mapActionsToProps>;

// Ref https://github.com/reduxjs/reselect#sharing-selectors-with-props-across-multiple-component-instances
const makeMapStateToProps = () => {
  const getProjectSearchOptions = makeGetProjectSearchOptions();

  const mapStateToProps = (state: IState, ownProps: ExternalProps) => {
    return getProjectSearchOptions(state, {
      // this will fall back to the authenticated user if not passed, via getUserId in selectors/projects.ts
      actAsUserId: ownProps.actAsUserId || undefined,
      initialProjectId: ownProps.value,
      hideArchivedProjects: ownProps.hideArchivedProjects,
      filterGroup: ownProps.filterGroup || 'all',
    });
  };
  return mapStateToProps;
};

type StateProps = ReturnType<ReturnType<typeof makeMapStateToProps>>;
type Props = ExternalProps & StateProps & ActionProps;

export const ProjectSearch: React.FC<Props> = props => {
  const dispatch = useDispatch<ThunkDispatch>();

  const {
    actAsUserId,
    clientProjectOptionsGroup,
    onChange,
    searchProjects,
    allProjectsLoaded,
    loadFullProject,
    recentProjects,
    filterGroup,
    onFilterGroupChange,
    pinnedProjects,
    ...rest
  } = props;

  useMountEffect(() => {
    if (props.value && loadFullProject) {
      dispatch(fetchProjectIdSmartly(props.value, { needUsers: true }));
    }
  });

  const handleSearch = useCallback(
    (searchString: string) => {
      return searchProjects(searchString, actAsUserId || null, filterGroup);
    },
    [searchProjects, actAsUserId, filterGroup]
  );

  const postProcessOptions = useCallback(
    (
      _: unknown,
      filterString: string,
      pushOption: (normalizedProject: ProjectDropdownOption) => void
    ) => {
      if (!allProjectsLoaded && filterString.length < 1) {
        pushOption(incompleteOption);
      }
    },
    [allProjectsLoaded]
  );

  const handleChange = useCallback(
    (value: string | number | null) => {
      const narrowedValue = value !== -1 && value != null && isNumber(value) ? value : null;

      if (narrowedValue) {
        onChange(narrowedValue);
        if (loadFullProject) {
          dispatch(fetchProjectIdSmartly(narrowedValue, { needUsers: true }));
        }
      }
    },
    [onChange, dispatch, loadFullProject]
  );

  const handleFilterGroupChange = useCallback(
    (filterGroup: FilterGroupValues) => {
      if (onFilterGroupChange) {
        onFilterGroupChange(filterGroup);
      }
    },
    [onFilterGroupChange]
  );

  const options = [pinnedProjects, recentProjects, ...clientProjectOptionsGroup].filter(notNull);

  const filterGroupsComponent = filterGroup && (
    <div style={{ padding: '12px 12px 0 12px' }}>
      <FilterGroups
        active={props.filterGroup || 'active'}
        groups={[
          { value: 'active' as const, name: 'Active' },
          { value: 'archived' as const, name: 'Archived' },
        ]}
        onChange={(filterGroup: FilterGroupValues) => {
          handleFilterGroupChange(filterGroup);
        }}
        useButtonGroup
      />
    </div>
  );

  return (
    <Search
      hideFromFullstory
      data-project-search
      wide
      popoverListHeader={filterGroupsComponent}
      popoverPosition="bottom-start"
      optionComponent={SelectOption}
      matchOption={matchProjectOption}
      options={options}
      optionHeight={24}
      onChange={handleChange}
      onSearch={handleSearch}
      onChangeSearchString={handleSearch}
      postProcessOptions={postProcessOptions}
      popoverFixed
      {...omit(
        rest,
        'dispatch',
        'actAsUserId',
        'userId',
        'loadFullProject',
        'hideArchivedProjects',
        'filterGroup',
        'onFilterGroupChange'
      )}
    />
  );
};

ProjectSearch.defaultProps = {
  placeholder: 'Jump to project...',
};

export default connect<StateProps, typeof mapActionsToProps, ExternalProps, IState>(
  makeMapStateToProps,
  mapActionsToProps
)(ProjectSearch);
